/**
 * seek-preview.js — window.SeekPreview: the picture in the seek bar's bubble.
 *
 * Hover over the timeline and see the frame you would land on — what every
 * desktop player shows (MPC-HC, PotPlayer, mpv+thumbfast, VLC 4) and what
 * /video-player did not.
 *
 * WHY THIS SHAPE (measured 23.09.2026, Chrome 153, RTX 4060, headed, under
 * load; bench: tests/scratch on barcode-stamped media — every frame carries
 * its own number in pixels, so "which frame did we show" is read, not assumed)
 *
 *   a second hidden <video>, exact seek        51 ms p50 on 1080p GOP 1 s —
 *                                              but 219 ms / 2.95 s max on a
 *                                              10 s GOP, 224 ms on 4K60 HEVC,
 *                                              and the MAIN video dropped 20 %
 *                                              (GOP 10 s) / 7 % (4K60) of its
 *                                              frames meanwhile; a 2 s sweep
 *                                              along the bar showed 5–9 frames
 *   the same element, keyframe seek            33–147 ms, still 9 % drops on 4K60
 *   a strip built by seeking a hidden <video>  7.5–30 s for 100 frames
 *   WebCodecs, the keyframe AT OR BEFORE t     9–11 ms p50 (17–30 ms p90) on
 *   (this module)                              all three, 1–12 ms to settle
 *                                              after a sweep, 126/126 positions
 *                                              drawn, main video 0 drops
 *   WebCodecs, the exact frame (on dwell)      44 ms 1080p GOP 1 s, 206 ms on a
 *                                              10 s GOP, 114 ms 4K60 — but 11 %
 *                                              drops on 4K60 WHILE PLAYING, so
 *                                              the caller asks for it only when
 *                                              that is cheap (see exactOk)
 *
 * The frame shown was verified by barcode in 100 % of 1 380 probes.
 *
 * WHAT IT READS. Only the index and the bytes of the frames it decodes:
 *   · mp4/mov/m4v — ftyp+moov (moov-at-end files too: they are read where they
 *     sit and appended back to back, the same synthetic stream the lazy demux
 *     of webcodecs-pipeline.js uses). Presentation times go through
 *     window.Mp4EditList — raw composition times are two B-frames late on
 *     every x264/phone file (measured: the barcode said so on 30/30 probes).
 *   · mkv/webm — EBML: SeekHead → Info → Tracks → Cues; without Cues (a
 *     MediaRecorder file) a bounded hop over cluster headers.
 * Fragmented mp4, laced Matroska video, encrypted or header-stripped tracks
 * are DECLINED with a reason — the bubble then shows the time only, as before.
 *
 *   var eng = await SeekPreview.open(fileOrBlob, { aspect: 16/9, cssWidth: 160 });
 *   // → engine | null (eng.why says why when null came back through openVerbose)
 *   eng.peek(t)                         → {canvas, t} of a cached keyframe or null
 *   eng.request(t, {exact:false})       → Promise<{canvas, t, exact}|null>
 *                                         (null = superseded by a newer request)
 *   eng.prefetch(n)                     → fills n evenly spaced keyframes in idle time
 *   eng.stats()                         → {keys, decoded, cached, exact, fails}
 *   eng.close()
 */
(function () {
'use strict';
if (window.SeekPreview) return;

var MP4BOX = '/vendor/mp4box/mp4box.all.min.js';
var CACHE_MAX = 160;              // thumbnails kept (≈ 160 × 58 KB at 1× = 9 MB)
var DECODE_TIMEOUT_MS = 4000;     // a decoder that answers nothing is a dead decoder
var EXACT_SPAN_MAX = 24 << 20;    // bytes read for one exact frame, at most
var MKV_CLUSTER_MAX = 48 << 20;   // one cluster read whole for exact frames, at most
var INDEX_BUDGET_MS = 6000;       // cluster hop without Cues

/* ─────────────────────────── loading helpers ─────────────────────────── */
function jsSrc(rel) { return (typeof window.__jsSrc === 'function') ? window.__jsSrc(rel) : rel; }
function loadScript(src) {
    return new Promise(function (res, rej) {
        var s = document.createElement('script');
        s.src = src; s.async = true;
        s.onload = function () { res(); };
        s.onerror = function () { rej(new Error('load:' + src)); };
        document.head.appendChild(s);
    });
}
function ensureMp4Box() {
    if (window.MP4Box) return Promise.resolve();
    /* Same promise the frame extractor and the pipeline share, so one load
       serves every module on the page. */
    if (!window.__mp4boxLoadingPromise) window.__mp4boxLoadingPromise = loadScript(MP4BOX);
    return window.__mp4boxLoadingPromise;
}
var _elP = null, _orP = null;
function ensureEditList() {
    if (window.Mp4EditList) return Promise.resolve();
    return _elP || (_elP = loadScript(jsSrc('/js/mp4-edit-list.js')).catch(function () { _elP = null; }));
}
function ensureOrientation() {
    if (window.VideoOrientation) return Promise.resolve();
    return _orP || (_orP = loadScript(jsSrc('/js/video-orientation.js')).catch(function () { _orP = null; }));
}
async function readBytes(blob, from, to) {
    return new Uint8Array(await blob.slice(from, to).arrayBuffer());
}

/* ─────────────────────────────── mp4 index ───────────────────────────── */
async function mp4TopLevel(file) {
    var boxes = [], off = 0, guard = 0, size = file.size;
    while (off < size && guard++ < 4096) {
        var h = await readBytes(file, off, Math.min(size, off + 16));
        if (h.length < 8) break;
        var dv = new DataView(h.buffer, h.byteOffset, h.byteLength);
        var bsz = dv.getUint32(0), hlen = 8;
        var type = String.fromCharCode(h[4], h[5], h[6], h[7]);
        if (bsz === 1) { if (h.length < 16) break; bsz = Number(dv.getBigUint64(8)); hlen = 16; }
        else if (bsz === 0) bsz = size - off;
        if (!(bsz >= hlen) || off + bsz > size) return { why: 'box-walk' };
        boxes.push({ type: type, start: off, size: bsz });
        off += bsz;
    }
    var out = { ftyp: null, moov: null, fragmented: false };
    boxes.forEach(function (b) {
        if (b.type === 'ftyp' && !out.ftyp) out.ftyp = b;
        else if (b.type === 'moov' && !out.moov) out.moov = b;
        else if (b.type === 'moof') out.fragmented = true;
    });
    if (!out.moov) return { why: 'no-moov' };
    if (out.fragmented) return { why: 'fragmented' };
    if (out.moov.size > (256 << 20)) return { why: 'moov-too-big' };
    return out;
}

async function mp4Index(file) {
    await Promise.all([ensureMp4Box(), ensureEditList(), ensureOrientation()]);
    if (!window.MP4Box) return { why: 'mp4box-missing' };
    var top = await mp4TopLevel(file);
    if (top.why) return top;
    var parts = [], total = 0, want = top.ftyp ? [top.ftyp, top.moov] : [top.moov];
    for (var i = 0; i < want.length; i++) {
        var u = await readBytes(file, want[i].start, want[i].start + want[i].size);
        parts.push(u); total += u.length;
    }
    var head = new Uint8Array(total), at = 0;
    parts.forEach(function (p) { head.set(p, at); at += p.length; });
    var buf = head.buffer; buf.fileStart = 0;
    var mp = window.MP4Box.createFile(), info = null;
    mp.onReady = function (inf) { info = inf; };
    mp.onError = function () {};
    mp.appendBuffer(buf);
    mp.flush();
    if (!info) return { why: 'moov-parse' };
    var vt = info.videoTracks && info.videoTracks[0];
    if (!vt) return { why: 'no-video-track' };
    var raw = null;
    try { raw = mp.getTrackSamplesInfo(vt.id); } catch (_) { raw = null; }
    if (!raw || !raw.length) return { why: 'no-samples' };
    var ts = vt.timescale;
    /* Media time → presentation time through the ONE edit-list reader. The
       samples keep their decode order; only `timestamp` is rewritten. */
    var samples = new Array(raw.length);
    for (var k = 0; k < raw.length; k++) {
        var x = raw[k];
        if (!(x.size > 0) || !isFinite(x.offset)) return { why: 'no-offsets' };
        samples[k] = { timestamp: Math.round(x.cts * 1e6 / ts), duration: Math.round(x.duration * 1e6 / ts),
                       off: x.offset, size: x.size, key: !!x.is_sync };
    }
    var edit = null;
    try {
        if (window.Mp4EditList) {
            var trk = null;
            (info.tracks || []).forEach(function (t) { if (t.id === vt.id) trk = t; });
            var plan = window.Mp4EditList.plan(trk || vt, info.timescale);
            edit = window.Mp4EditList.applyVideo(samples, plan);
            edit.shape = plan.shape;
        }
    } catch (_) { edit = null; }
    if (!edit) {                                   // reader unavailable: earliest time is 0
        var m0 = Infinity; samples.forEach(function (s) { if (s.timestamp < m0) m0 = s.timestamp; });
        samples.forEach(function (s) { s.timestamp -= m0; });
    }
    var entry = null;
    try { entry = mp.getTrackById(vt.id).mdia.minf.stbl.stsd.entries[0]; } catch (_) {}
    var box = entry && (entry.avcC || entry.hvcC || entry.vpcC || entry.av1C);
    var desc = null;
    if (box && window.DataStream) {
        try {
            var ds = new window.DataStream(undefined, 0, window.DataStream.BIG_ENDIAN);
            box.write(ds);
            desc = new Uint8Array(ds.buffer, 8);   // the box body, without its own header
        } catch (_) { desc = null; }
    }
    if (entry && (entry.type === 'encv' || entry.sinf)) return { why: 'encrypted' };
    var rot = 0;
    try { rot = window.VideoOrientation ? window.VideoOrientation.fromTkhd(mp, vt.id) : 0; } catch (_) {}
    var keys = [];
    for (var j = 0; j < samples.length; j++) {
        var s = samples[j];
        if (s.key && s.timestamp >= 0) keys.push({ t: s.timestamp / 1e6, i: j });
    }
    keys.sort(function (a, b) { return a.t - b.t; });
    return {
        container: 'mp4', codec: vt.codec, w: vt.video.width, h: vt.video.height, desc: desc, rotation: rot,
        samples: samples, keys: keys, editShape: edit ? edit.shape : 'none',
        fetchKey: function (key) {
            var sm = samples[key.i];
            return readBytes(file, sm.off, sm.off + sm.size).then(function (b) { return [{ data: b, us: sm.timestamp, key: true }]; });
        },
        /* The run from the keyframe to the last sample that can still be the
           frame at t. With B-frames the frame shown at t may be DECODED after a
           later P-frame, so the run ends at the highest decode index among the
           samples presented at or before t — references always come earlier. */
        fetchRun: async function (key, tUs) {
            var i0 = key.i, last = i0;
            for (var q = i0 + 1; q < samples.length; q++) {
                if (samples[q].key) break;
                if (samples[q].timestamp <= tUs) last = q;
                if (samples[q].timestamp > tUs + 2e6) break;       // well past t in presentation
            }
            var lo = samples[i0].off, hi = lo;
            for (var r = i0; r <= last; r++) { lo = Math.min(lo, samples[r].off); hi = Math.max(hi, samples[r].off + samples[r].size); }
            if (hi - lo > EXACT_SPAN_MAX) return null;
            var span = await readBytes(file, lo, hi);
            var out = [];
            for (var z = i0; z <= last; z++) {
                var sz = samples[z];
                out.push({ data: span.subarray(sz.off - lo, sz.off - lo + sz.size), us: sz.timestamp, key: sz.key });
            }
            return out;
        }
    };
}

/* ────────────────────────────── mkv index ────────────────────────────── */
var ID = {
    EBML: 0x1A45DFA3, SEGMENT: 0x18538067, SEEKHEAD: 0x114D9B74, SEEK: 0x4DBB, SEEKID: 0x53AB, SEEKPOS: 0x53AC,
    INFO: 0x1549A966, TCSCALE: 0x2AD7B1, TRACKS: 0x1654AE6B, TRACK: 0xAE, TNUM: 0xD7, TTYPE: 0x83,
    CODECID: 0x86, CODECPRIV: 0x63A2, VIDEO: 0xE0, PXW: 0xB0, PXH: 0xBA, ENCODINGS: 0x6D80,
    CUES: 0x1C53BB6B, CUEPOINT: 0xBB, CUETIME: 0xB3, CUETRACKPOS: 0xB7, CUETRACK: 0xF7, CUECLUSTER: 0xF1, CUEREL: 0xF0,
    CLUSTER: 0x1F43B675, TIMECODE: 0xE7, SIMPLEBLOCK: 0xA3, BLOCKGROUP: 0xA0, BLOCK: 0xA1, REFBLOCK: 0xFB
};
var TOP_LEVEL = {}; [ID.SEEKHEAD, ID.INFO, ID.TRACKS, ID.CUES, ID.CLUSTER, 0x1941A469, 0x1043A770, 0x1254C367].forEach(function (x) { TOP_LEVEL[x] = 1; });

function vint(b, o, keepMarker) {
    if (o >= b.length) return null;
    var first = b[o], len = 1, mask = 0x80;
    while (len <= 8 && !(first & mask)) { len++; mask >>= 1; }
    if (len > 8 || o + len > b.length) return null;
    var v = keepMarker ? first : (first & (mask - 1)), allOnes = (first & (mask - 1)) === (mask - 1);
    for (var i = 1; i < len; i++) { v = v * 256 + b[o + i]; if (b[o + i] !== 0xFF) allOnes = false; }
    return { v: v, len: len, unknown: !keepMarker && allOnes };
}
function el(b, o) {
    var id = vint(b, o, true); if (!id) return null;
    var sz = vint(b, o + id.len, false); if (!sz) return null;
    return { id: id.v, size: sz.unknown ? -1 : sz.v, hdr: id.len + sz.len, at: o };
}
function uint(b, o, n) { var v = 0; for (var i = 0; i < n; i++) v = v * 256 + b[o + i]; return v; }
function children(b, from, to, fn) {
    var o = from;
    while (o < to) {
        var e = el(b, o); if (!e || e.size < 0) return;
        fn(e, o + e.hdr, o + e.hdr + e.size);
        o += e.hdr + e.size;
    }
}
function hex2(n) { return ('0' + (n & 255).toString(16)).slice(-2); }
function reverse32(x) { var r = 0; for (var i = 0; i < 32; i++) { r = (r * 2) + (x & 1); x = x >>> 1; } return r >>> 0; }
function hevcCodec(p) {
    if (!p || p.length < 13) return 'hvc1.1.6.L93.B0';
    var space = (p[1] >> 6) & 3, tier = (p[1] >> 5) & 1, prof = p[1] & 31;
    var compat = reverse32(uint(p, 2, 4));
    var cons = []; for (var i = 6; i < 12; i++) cons.push(p[i]);
    while (cons.length && cons[cons.length - 1] === 0) cons.pop();
    return 'hvc1.' + ['', 'A', 'B', 'C'][space] + prof + '.' + compat.toString(16) + '.' + (tier ? 'H' : 'L') + p[12] +
        (cons.length ? '.' + cons.map(function (c) { return c.toString(16); }).join('.') : '');
}
function av1Codec(p) {
    if (!p || p.length < 4) return 'av01.0.08M.08';
    var prof = (p[1] >> 5) & 7, lvl = p[1] & 31, tier = (p[2] >> 7) & 1, hbd = (p[2] >> 6) & 1, twelve = (p[2] >> 5) & 1;
    return 'av01.' + prof + '.' + ('0' + lvl).slice(-2) + (tier ? 'H' : 'M') + '.' + (twelve ? '12' : hbd ? '10' : '08');
}
function mkvCodec(id, priv) {
    if (id === 'V_MPEG4/ISO/AVC') return priv && priv.length >= 4 ? { codec: 'avc1.' + hex2(priv[1]) + hex2(priv[2]) + hex2(priv[3]), desc: priv } : null;
    if (id === 'V_MPEGH/ISO/HEVC') return priv ? { codec: hevcCodec(priv), desc: priv } : null;
    if (id === 'V_VP9') return { codec: 'vp09.00.51.08', desc: null, alt: 'vp09.02.51.10' };
    if (id === 'V_VP8') return { codec: 'vp8', desc: null };
    if (id === 'V_AV1') return { codec: av1Codec(priv), desc: null };
    return null;
}

/* One block's frame: SimpleBlock or the Block of a BlockGroup. Laced video is
   declined — it essentially never occurs, and a wrong split is a broken frame. */
function parseBlock(b, from, to, track, clusterTc) {
    var tn = vint(b, from, false); if (!tn || tn.v !== track) return null;
    var o = from + tn.len;
    var rel = (b[o] << 8) | b[o + 1]; if (rel & 0x8000) rel -= 0x10000;
    var flags = b[o + 2];
    if ((flags >> 1) & 3) return { laced: true };
    return { tc: clusterTc + rel, key: !!(flags & 0x80), data: b.subarray(o + 3, to) };
}

async function mkvIndex(file) {
    var size = file.size;
    var head = await readBytes(file, 0, Math.min(size, 1 << 16));
    var e0 = el(head, 0);
    if (!e0 || e0.id !== ID.EBML) return { why: 'not-ebml' };
    var segE = el(head, e0.hdr + e0.size);
    if (!segE || segE.id !== ID.SEGMENT) return { why: 'no-segment' };
    var seg = segE.at + segE.hdr, segEnd = segE.size < 0 ? size : Math.min(size, seg + segE.size);
    var pos = { }, firstCluster = -1, p = seg, walked = 0;
    // walk top-level headers until the first Cluster (everything before it is small)
    while (p < segEnd && walked++ < 64) {
        var hb = await readBytes(file, p, Math.min(segEnd, p + 16));
        var e = el(hb, 0); if (!e) break;
        if (e.id === ID.CLUSTER) { firstCluster = p; break; }
        if (!pos[e.id]) pos[e.id] = { at: p, size: e.size, hdr: e.hdr };
        if (e.size < 0) break;
        p += e.hdr + e.size;
    }
    async function body(x) { return readBytes(file, x.at + x.hdr, x.at + x.hdr + Math.min(x.size, 64 << 20)); }
    // SeekHead → where Cues (and anything we missed) live
    if (pos[ID.SEEKHEAD]) {
        var sh = await body(pos[ID.SEEKHEAD]);
        children(sh, 0, sh.length, function (c, a, z) {
            if (c.id !== ID.SEEK) return;
            var sid = 0, spos = -1;
            children(sh, a, z, function (d, da, dz) {
                if (d.id === ID.SEEKID) sid = uint(sh, da, dz - da);
                else if (d.id === ID.SEEKPOS) spos = uint(sh, da, dz - da);
            });
            if (sid && spos >= 0 && !pos[sid] && (sid === ID.CUES || sid === ID.TRACKS || sid === ID.INFO)) pos[sid] = { at: seg + spos, lazy: true };
        });
    }
    for (var k in pos) {
        if (pos[k].lazy) {
            var hb2 = await readBytes(file, pos[k].at, Math.min(size, pos[k].at + 16));
            var e2 = el(hb2, 0);
            if (!e2 || e2.id !== +k || e2.size < 0) { delete pos[k]; continue; }
            pos[k] = { at: pos[k].at, size: e2.size, hdr: e2.hdr };
        }
    }
    if (!pos[ID.TRACKS]) return { why: 'no-tracks' };
    var scale = 1e6;
    if (pos[ID.INFO]) {
        var ib = await body(pos[ID.INFO]);
        children(ib, 0, ib.length, function (c, a, z) { if (c.id === ID.TCSCALE) scale = uint(ib, a, z - a) || 1e6; });
    }
    var tb = await body(pos[ID.TRACKS]), vtrack = null;
    children(tb, 0, tb.length, function (c, a, z) {
        if (c.id !== ID.TRACK || vtrack) return;
        var t = { num: 0, type: 0, codecId: '', priv: null, w: 0, h: 0, enc: false };
        children(tb, a, z, function (d, da, dz) {
            if (d.id === ID.TNUM) t.num = uint(tb, da, dz - da);
            else if (d.id === ID.TTYPE) t.type = uint(tb, da, dz - da);
            else if (d.id === ID.CODECID) t.codecId = String.fromCharCode.apply(null, tb.subarray(da, dz)).replace(/\0+$/, '');
            else if (d.id === ID.CODECPRIV) t.priv = tb.slice(da, dz);
            else if (d.id === ID.ENCODINGS) t.enc = true;
            else if (d.id === ID.VIDEO) children(tb, da, dz, function (v, va, vz) {
                if (v.id === ID.PXW) t.w = uint(tb, va, vz - va); else if (v.id === ID.PXH) t.h = uint(tb, va, vz - va);
            });
        });
        if (t.type === 1) vtrack = t;
    });
    if (!vtrack) return { why: 'no-video-track' };
    if (vtrack.enc) return { why: 'content-encoding' };
    var cc = mkvCodec(vtrack.codecId, vtrack.priv);
    if (!cc) return { why: 'codec:' + vtrack.codecId };
    var toSec = function (tc) { return tc * scale / 1e9; };
    var keys = [];
    if (pos[ID.CUES]) {
        var cb = await body(pos[ID.CUES]);
        children(cb, 0, cb.length, function (c, a, z) {
            if (c.id !== ID.CUEPOINT) return;
            var ct = -1;
            children(cb, a, z, function (d, da, dz) {
                if (d.id === ID.CUETIME) ct = uint(cb, da, dz - da);
                else if (d.id === ID.CUETRACKPOS) {
                    var tr = 0, cl = -1, rel = -1;
                    children(cb, da, dz, function (f, fa, fz) {
                        if (f.id === ID.CUETRACK) tr = uint(cb, fa, fz - fa);
                        else if (f.id === ID.CUECLUSTER) cl = uint(cb, fa, fz - fa);
                        else if (f.id === ID.CUEREL) rel = uint(cb, fa, fz - fa);
                    });
                    if (tr === vtrack.num && cl >= 0 && ct >= 0) keys.push({ t: toSec(ct), tc: ct, cluster: seg + cl, rel: rel });
                }
            });
        });
    }
    var indexed = keys.length ? 'cues' : 'hop';
    if (!keys.length && firstCluster >= 0) {
        // No Cues (MediaRecorder output): hop cluster to cluster, reading only
        // the head of each, and keep the ones that open on a keyframe.
        var q = firstCluster, t0 = Date.now(), n = 0;
        while (q < segEnd && n++ < 20000 && Date.now() - t0 < INDEX_BUDGET_MS) {
            var cbh = await readBytes(file, q, Math.min(size, q + 4096));
            var ce = el(cbh, 0);
            if (!ce || ce.id !== ID.CLUSTER) break;
            var ctc = -1, kf = null, o2 = ce.hdr;
            while (o2 < cbh.length) {
                var ch = el(cbh, o2); if (!ch || ch.size < 0) break;
                var da2 = o2 + ch.hdr, dz2 = da2 + ch.size;
                if (ch.id === ID.TIMECODE && dz2 <= cbh.length) ctc = uint(cbh, da2, ch.size);
                else if (ch.id === ID.SIMPLEBLOCK && ctc >= 0) {
                    var blk = parseBlock(cbh, da2, Math.min(dz2, cbh.length), vtrack.num, ctc);
                    if (blk && !blk.laced) { kf = blk; break; }
                }
                o2 = dz2;
            }
            if (kf && kf.key) keys.push({ t: toSec(kf.tc), tc: kf.tc, cluster: q, rel: -1 });
            if (ce.size < 0) break;                  // unknown-size cluster: cannot hop over it
            q += ce.hdr + ce.size;
        }
        if (Date.now() - t0 >= INDEX_BUDGET_MS) indexed = 'hop-partial';
    }
    if (!keys.length) return { why: 'no-keyframes' };
    keys.sort(function (a, b) { return a.t - b.t; });

    /* Every video block of the clusters from `key.cluster` on, up to the next
       keyframe after the first one (or the byte ceiling). */
    async function clusterBlocks(start, stopUs, firstOnly) {
        var out = [], q2 = start, used = 0, sawKey = false;
        while (q2 < segEnd && used < MKV_CLUSTER_MAX) {
            var h = await readBytes(file, q2, Math.min(size, q2 + 16));
            var ce2 = el(h, 0);
            if (!ce2 || ce2.id !== ID.CLUSTER || ce2.size < 0) break;
            var cbody = await readBytes(file, q2 + ce2.hdr, Math.min(size, q2 + ce2.hdr + ce2.size));
            used += cbody.length;
            var ctc2 = 0, stop = false;
            children(cbody, 0, cbody.length, function (c, a, z) {
                if (stop) return;
                if (c.id === ID.TIMECODE) ctc2 = uint(cbody, a, z - a);
                else if (c.id === ID.SIMPLEBLOCK || c.id === ID.BLOCKGROUP) {
                    var blk2 = null, hasRef = false;
                    if (c.id === ID.SIMPLEBLOCK) blk2 = parseBlock(cbody, a, z, vtrack.num, ctc2);
                    else children(cbody, a, z, function (g, ga, gz) {
                        if (g.id === ID.BLOCK) blk2 = parseBlock(cbody, ga, gz, vtrack.num, ctc2);
                        else if (g.id === ID.REFBLOCK) hasRef = true;
                    });
                    if (!blk2 || blk2.laced) return;
                    if (c.id === ID.BLOCKGROUP) blk2.key = !hasRef;
                    if (blk2.key && sawKey) { stop = true; return; }
                    if (blk2.key) sawKey = true;
                    if (!sawKey) return;
                    var us = Math.round(toSec(blk2.tc) * 1e6);
                    out.push({ data: blk2.data, us: us, key: blk2.key });
                    if (firstOnly) { stop = true; return; }
                    if (stopUs != null && us > stopUs + 2e6 && !blk2.key) stop = true;
                }
            });
            if (stop) break;
            q2 += ce2.hdr + ce2.size;
        }
        return out;
    }
    return {
        container: 'mkv', codec: cc.codec, altCodec: cc.alt || null, w: vtrack.w, h: vtrack.h, desc: cc.desc, rotation: 0,
        keys: keys, indexed: indexed,
        fetchKey: async function (key) {
            if (key.rel >= 0) {
                // Cues pointed straight at the block: read just that element.
                var hb3 = await readBytes(file, key.cluster, Math.min(size, key.cluster + 16));
                var ce3 = el(hb3, 0);
                if (ce3 && ce3.id === ID.CLUSTER) {
                    var at3 = key.cluster + ce3.hdr + key.rel;
                    var bh = await readBytes(file, at3, Math.min(size, at3 + 16));
                    var be = el(bh, 0);
                    if (be && be.size > 0 && (be.id === ID.SIMPLEBLOCK || be.id === ID.BLOCKGROUP)) {
                        var bb = await readBytes(file, at3, Math.min(size, at3 + be.hdr + be.size));
                        var got = null;
                        if (be.id === ID.SIMPLEBLOCK) got = parseBlock(bb, be.hdr, bb.length, vtrack.num, key.tc - 0);
                        else children(bb, be.hdr, bb.length, function (g, ga, gz) { if (g.id === ID.BLOCK) got = parseBlock(bb, ga, gz, vtrack.num, key.tc); });
                        if (got && !got.laced) return [{ data: got.data, us: Math.round(key.t * 1e6), key: true }];
                    }
                }
            }
            var run = await clusterBlocks(key.cluster, null, true);
            return run.length ? [run[0]] : null;
        },
        fetchRun: async function (key, tUs) {
            var run = await clusterBlocks(key.cluster, tUs);
            if (!run.length) return null;
            // decode order is storage order; keep up to the last block shown at or before t
            var last = 0;
            for (var i = 0; i < run.length; i++) if (run[i].us <= tUs) last = i;
            return run.slice(0, last + 1);
        }
    };
}

/* ───────────────────────────── the engine ────────────────────────────── */
function isMkv(file) {
    var n = (file && file.name || '').toLowerCase(), t = (file && file.type || '').toLowerCase();
    return /\.(mkv|webm|mka|mk3d)$/.test(n) || /matroska|webm/.test(t);
}

async function sniff(file) {
    var b = await readBytes(file, 0, Math.min(file.size, 12));
    if (b.length >= 4 && b[0] === 0x1A && b[1] === 0x45 && b[2] === 0xDF && b[3] === 0xA3) return 'mkv';
    if (b.length >= 8) {
        var tag = String.fromCharCode(b[4], b[5], b[6], b[7]);
        if (/^(ftyp|moov|mdat|free|skip|wide|pnot)$/.test(tag)) return 'mp4';
    }
    return isMkv(file) ? 'mkv' : '';
}

function support() {
    return typeof window.VideoDecoder === 'function' && typeof window.EncodedVideoChunk === 'function';
}

async function openVerbose(file, opts) {
    opts = opts || {};
    if (!support()) return { why: 'no-webcodecs' };
    if (!file || !file.size) return { why: 'no-file' };
    var t0 = performance.now();
    var kind = await sniff(file), idx;
    try {
        idx = kind === 'mp4' ? await mp4Index(file) : kind === 'mkv' ? await mkvIndex(file) : { why: 'container' };
    } catch (e) {
        idx = { why: 'index:' + String(e && e.message || e).slice(0, 40) };
    }
    if (idx.why) return idx;
    var cfg = { codec: idx.codec, codedWidth: idx.w || undefined, codedHeight: idx.h || undefined };
    if (idx.desc) cfg.description = idx.desc;
    var sup = null;
    try { sup = await VideoDecoder.isConfigSupported(cfg); } catch (_) { sup = null; }
    if ((!sup || !sup.supported) && idx.altCodec) {
        cfg.codec = idx.altCodec;
        try { sup = await VideoDecoder.isConfigSupported(cfg); } catch (_) { sup = null; }
    }
    if (!sup || !sup.supported) return { why: 'codec:' + idx.codec };
    await ensureOrientation();
    var eng = new Engine(idx, cfg, opts);
    eng.indexMs = Math.round(performance.now() - t0);
    /* «Configure accepted» is not «decodes»: on Android a software H.264
       hint is accepted and fails minutes later (DecoderHintLadderConventionTest).
       One real keyframe must come out before we promise pictures. */
    var first = await eng.request(idx.keys[Math.min(idx.keys.length - 1, idx.keys.length >> 1)].t, {});
    if (!first) { eng.close(); return { why: eng.lastError || 'decoder' }; }
    return { engine: eng };
}

function Engine(idx, cfg, opts) {
    this.idx = idx;
    this.cfg = cfg;
    this.aspect = opts.aspect || (idx.w && idx.h ? idx.w / idx.h : 16 / 9);
    var cssW = opts.cssWidth || 160, dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
    // thumbnails are drawn in DISPLAY orientation, sized by the aspect the page sees
    if (this.aspect >= 1) { this.tw = Math.round(cssW * dpr); this.th = Math.round(cssW * dpr / this.aspect); }
    else { this.th = Math.round(cssW * dpr); this.tw = Math.round(cssW * dpr * this.aspect); }
    this.cache = new Map();          // key index → canvas
    this.exactCache = [];            // [{us, canvas}] last few exact frames
    this.dec = null; this.decBroken = 0;
    this.busy = false; this.want = null; this.waiters = null;
    this.prefetchQ = [];
    this.closed = false;
    this.n = { decoded: 0, exact: 0, fails: 0 };
    this.lastError = '';
    this._ticket = 0;                // key decodes carry NEGATIVE tickets as timestamps,
                                     // so a late one can never pass for a real frame
}

Engine.prototype.keyIndexAt = function (t) {
    var ks = this.idx.keys, lo = 0, hi = ks.length - 1, hit = 0;
    while (lo <= hi) { var m = (lo + hi) >> 1; if (ks[m].t <= t + 1e-4) { hit = m; lo = m + 1; } else hi = m - 1; }
    return hit;
};

Engine.prototype.peek = function (t) {
    var i = this.keyIndexAt(t), c = this.cache.get(i);
    if (!c) return null;
    this.cache.delete(i); this.cache.set(i, c);        // LRU touch
    return { canvas: c, t: this.idx.keys[i].t, exact: false };
};

Engine.prototype._decoder = function () {
    if (this.dec && this.dec.state === 'configured') return this.dec;
    var self = this;
    this._outs = {};
    this.dec = new VideoDecoder({
        output: function (fr) {
            var w = self._outs[fr.timestamp];
            if (w) { delete self._outs[fr.timestamp]; w(fr); }
            else if (self._sink) self._sink(fr);
            else fr.close();
        },
        error: function (e) { self.lastError = 'decoder:' + String(e && (e.name || e.message) || e).slice(0, 40); }
    });
    this.dec.configure(this.cfg);
    return this.dec;
};

Engine.prototype._thumb = function (frame) {
    var c = document.createElement('canvas');
    c.width = this.tw; c.height = this.th;
    var ctx = c.getContext('2d');
    var VO = window.VideoOrientation;
    var f = frame;
    if (VO && this.idx.rotation) f = VO.apply(frame, this.idx.rotation);
    if (VO) VO.draw(ctx, f, 0, 0, this.tw, this.th); else ctx.drawImage(f, 0, 0, this.tw, this.th);
    try { f.close(); } catch (_) {}
    return c;
};

Engine.prototype._withTimeout = function (p) {
    return Promise.race([p, new Promise(function (_, rej) { setTimeout(function () { rej(new Error('timeout')); }, DECODE_TIMEOUT_MS); })]);
};

/* One keyframe → one thumbnail. decode + flush: without the flush a hardware
   decoder holds a lone frame back (measured: 64 ms vs 9 ms). */
Engine.prototype._decodeKey = async function (ki) {
    var key = this.idx.keys[ki];
    var parts = await this.idx.fetchKey(key);
    if (!parts || !parts.length) throw new Error('read');
    var dec = this._decoder(), self = this;
    var ts = -(++this._ticket);
    var got = new Promise(function (res) { self._outs[ts] = res; });
    dec.decode(new EncodedVideoChunk({ type: 'key', timestamp: ts, data: parts[0].data }));
    var fr;
    try {
        await this._withTimeout(dec.flush());
        fr = await this._withTimeout(got);
    } finally { delete this._outs[ts]; }
    this.n.decoded++;
    return this._thumb(fr);
};

/* The exact frame at t: the run from its keyframe, decoded in order; the
   latest output at or before t wins. */
Engine.prototype._decodeExact = async function (ki, tUs) {
    var key = this.idx.keys[ki];
    var run = await this.idx.fetchRun(key, tUs);
    if (!run || !run.length) throw new Error('run');
    var dec = this._decoder(), best = null, bestUs = -Infinity;
    this._sink = function (fr) {
        // negative = a key decode that timed out earlier; never a frame of this run
        if (fr.timestamp >= 0 && fr.timestamp <= tUs + 1 && fr.timestamp > bestUs) { if (best) best.close(); best = fr; bestUs = fr.timestamp; }
        else fr.close();
    };
    try {
        for (var i = 0; i < run.length; i++) {
            if (this.closed) break;
            dec.decode(new EncodedVideoChunk({ type: run[i].key ? 'key' : 'delta', timestamp: run[i].us, data: run[i].data }));
            while (dec.decodeQueueSize > 8) await new Promise(function (r) { setTimeout(r, 1); });
        }
        await this._withTimeout(dec.flush());
    } finally { this._sink = null; }
    if (!best) throw new Error('no-frame');
    this.n.exact++;
    return { canvas: this._thumb(best), us: bestUs };
};

/* Newest request wins: while one decode runs, only the LAST asked-for
   position waits; everything in between resolves null (it is stale). */
Engine.prototype.request = function (t, o) {
    if (this.closed) return Promise.resolve(null);
    var exact = !!(o && o.exact), ki = this.keyIndexAt(t);
    if (!exact) { var hit = this.peek(t); if (hit) return Promise.resolve(hit); }
    else {
        var tUs = Math.round(t * 1e6);
        for (var e = 0; e < this.exactCache.length; e++) if (Math.abs(this.exactCache[e].want - tUs) < 1) return Promise.resolve(this.exactCache[e].res);
    }
    var self = this;
    if (this.want) this.want.resolve(null);
    return new Promise(function (resolve) {
        self.want = { t: t, ki: ki, exact: exact, resolve: resolve };
        self._pump();
    });
};

Engine.prototype._pump = async function () {
    if (this.busy || this.closed) return;
    var job = this.want;
    var fromPrefetch = false;
    if (!job) {
        var pk = this.prefetchQ.shift();
        if (pk == null) return;
        if (this.cache.has(pk)) { this._later(); return; }
        job = { ki: pk, exact: false, resolve: function () {} };
        fromPrefetch = true;
    } else this.want = null;
    this.busy = true;
    try {
        if (job.exact) {
            var tUs = Math.round(job.t * 1e6);
            var ex = await this._decodeExact(job.ki, tUs);
            var res = { canvas: ex.canvas, t: ex.us / 1e6, exact: true };
            this.exactCache.push({ want: tUs, res: res });
            if (this.exactCache.length > 6) this.exactCache.shift();
            job.resolve(res);
        } else {
            var c = this.cache.get(job.ki) || await this._decodeKey(job.ki);
            this._put(job.ki, c);
            job.resolve({ canvas: c, t: this.idx.keys[job.ki].t, exact: false });
        }
        this.decBroken = 0;
    } catch (e) {
        this.n.fails++;
        if (!this.lastError) this.lastError = String(e && e.message || e).slice(0, 40);
        // a decoder that errored is closed by the browser; rebuild once, then give up
        try { if (this.dec) this.dec.close(); } catch (_) {}
        this.dec = null;
        if (++this.decBroken >= 3) { this.closed = true; }
        job.resolve(null);
    } finally {
        this.busy = false;
    }
    if (this.want) this._pump();
    else if (this.prefetchQ.length) this._later();
    void fromPrefetch;
};

Engine.prototype._later = function () {
    var self = this;
    var run = function () { self._pump(); };
    if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 400 });
    else setTimeout(run, 30);
};

Engine.prototype._put = function (ki, c) {
    this.cache.delete(ki); this.cache.set(ki, c);
    while (this.cache.size > CACHE_MAX) this.cache.delete(this.cache.keys().next().value);
};

/* Fill `n` evenly spaced keyframes, one at a time, only while nobody is
   asking for anything — a pointer request always jumps the queue. */
Engine.prototype.prefetch = function (n) {
    var ks = this.idx.keys, count = Math.min(n || 60, ks.length, CACHE_MAX);
    if (!count || this.closed) return;
    var dur = ks[ks.length - 1].t || 1, seen = {};
    for (var i = 0; i < count; i++) {
        var ki = this.keyIndexAt((i + 0.5) * dur / count);
        if (!seen[ki]) { seen[ki] = 1; this.prefetchQ.push(ki); }
    }
    this._later();
};

Engine.prototype.stats = function () {
    return { keys: this.idx.keys.length, decoded: this.n.decoded, cached: this.cache.size, exact: this.n.exact, fails: this.n.fails };
};
Engine.prototype.info = function () {
    return { container: this.idx.container, codec: this.cfg.codec, keys: this.idx.keys.length, indexed: this.idx.indexed || 'moov',
             edit: this.idx.editShape || '', rotation: this.idx.rotation || 0, indexMs: this.indexMs || 0 };
};
Engine.prototype.close = function () {
    this.closed = true;
    if (this.want) { this.want.resolve(null); this.want = null; }
    this.prefetchQ = [];
    try { if (this.dec) this.dec.close(); } catch (_) {}
    this.dec = null;
    this.cache.clear(); this.exactCache = [];
};

window.SeekPreview = {
    support: support,
    openVerbose: openVerbose,
    open: function (file, opts) { return openVerbose(file, opts).then(function (r) { return r.engine || null; }); },
    _mkvIndex: mkvIndex, _mp4Index: mp4Index, _hevcCodec: hevcCodec, _av1Codec: av1Codec
};
})();
